import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { CommentItem } from '@/components/Map/Drawer/BioEntityDrawer/Comments/CommentItem.component';
import { ZERO } from '@/constants/common';
import ReactionTypeEnum from '@/utils/reaction/ReactionTypeEnum';
import React from 'react';
import { AnnotationItemList } from '@/components/Map/Drawer/BioEntityDrawer/AnnotationItem/AnnotationItemList.component';
import {
  currentDrawerReactionSelector,
  currentDrawerReactionCommentsSelector,
} from '@/redux/reactions/reactions.selector';
import { DrawerHeadingBackwardButton } from '@/shared/DrawerHeadingBackwardButton';
import { openSearchDrawerWithSelectedTab, selectTab } from '@/redux/drawer/drawer.slice';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { currentSelectedSearchElement } from '@/redux/drawer/drawer.selectors';
import { hasPrivilegeToWriteProjectSelector } from '@/redux/user/user.selectors';
import { openEditReactionModal } from '@/redux/modal/modal.slice';
import { Button } from '@/shared/Button';
import { ConnectedBioEntitiesList } from './ConnectedBioEntitiesList';

export const ReactionDrawer = (): React.ReactNode => {
  const dispatch = useAppDispatch();
  const reaction = useAppSelector(currentDrawerReactionSelector);
  const commentsData = useAppSelector(currentDrawerReactionCommentsSelector);
  const currentSelectedSearchQuery = useAppSelector(currentSelectedSearchElement);
  const hasPrivilegeToWriteProject = useAppSelector(hasPrivilegeToWriteProjectSelector);
  if (!reaction) {
    return null;
  }

  const isCommentAvailable = commentsData.length > ZERO;
  type ReactionTypeKey = keyof typeof ReactionTypeEnum;
  const type = ReactionTypeEnum[reaction.sboTerm as ReactionTypeKey];

  const editReaction = (): void => {
    dispatch(openEditReactionModal(reaction));
  };

  const navigateToGroupedSearchResults = (): void => {
    dispatch(openSearchDrawerWithSelectedTab(''));
    dispatch(selectTab(currentSelectedSearchQuery));
  };

  return (
    <div className="h-full max-h-full" data-testid="reaction-drawer">
      <DrawerHeadingBackwardButton backwardFunction={navigateToGroupedSearchResults}>
        <div className="flex items-center">
          <span>
            <span className="font-normal">Reaction:</span>&nbsp;{reaction.reactionId}
          </span>
          {hasPrivilegeToWriteProject && (
            <Button className="ml-5 max-h-8" variantStyles="ghost" onClick={editReaction}>
              Edit
            </Button>
          )}
        </div>
      </DrawerHeadingBackwardButton>
      <div className="flex h-[calc(100%-93px)] max-h-[calc(100%-93px)] flex-col gap-6 overflow-y-auto p-6">
        <div className="text-sm font-normal">
          Type: <b className="font-semibold">{type}</b>
        </div>
        <hr className="border-b border-b-divide" />
        {reaction.notes && (
          <div
            className="text-sm font-normal"
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: reaction.notes }}
          />
        )}

        <AnnotationItemList annotations={reaction.references} />
        <hr className="border-b border-b-divide" />
        <ConnectedBioEntitiesList reaction={reaction} />
        {isCommentAvailable && <div className="font-bold"> Comments</div>}
        {isCommentAvailable &&
          commentsData.map(comment => <CommentItem key={comment.id} comment={comment} />)}
      </div>
    </div>
  );
};
